/**
 * @file api.ts
 * @brief Notification settings (categories on/off, quiet hours) for web and desktop.
 *
 * @details
 * BACKEND src/features/push/routes.js GET/PUT /api/push/preferences. The
 * settings live on the account, so they apply to the phone and the desktop app
 * alike (ADR-032, 09-10-2026).
 *
 * @author DES
 * @date 09-10-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "@/shared/api/client";

export type PushCategoryId =
  | "meetings"
  | "reminders"
  | "collaboration"
  | "tasks"
  | "billing"
  | "security"
  | "retention";

export interface PushCategory {
  id: PushCategoryId;
  label: string;
  /** Security cannot be turned off. */
  locked: boolean;
}

export interface PushPreferences {
  categories: PushCategory[];
  disabledCategories: PushCategoryId[];
  quietHours: boolean;
  timeZone: string | null;
}

export type PushPreferencesPatch = Partial<Pick<PushPreferences, "disabledCategories" | "quietHours">> & {
  timeZone?: string;
};

const KEY = ["push", "preferences"] as const;

/** This computer's IANA time zone. */
export function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export function fetchPushPreferences(): Promise<PushPreferences> {
  return apiGet<PushPreferences>("/api/push/preferences");
}

export function savePushPreferences(patch: PushPreferencesPatch): Promise<PushPreferences> {
  return apiPut<PushPreferences, PushPreferencesPatch>("/api/push/preferences", patch);
}

export function usePushPreferences() {
  return useQuery({ queryKey: KEY, queryFn: fetchPushPreferences });
}

export function useSavePushPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: savePushPreferences,
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}
